/**
 * Opening project files from the operating system: once the app is installed, double-clicking a
 * `.cutroom` file (or "Open with → Cutroom") launches it with that file (File Handling API, see
 * `public/manifest.webmanifest`). Projects open straight away when this device already has access
 * to their folder; otherwise the start screen asks for the one click a permission prompt or the
 * folder picker needs.
 */
import { create } from 'zustand';
import { projectStore } from '../projects/setup.ts';
import type { LaunchedProject } from '../projects/types.ts';
import { useEditor } from './editor-store.ts';
import { closeProject, openLaunchedProject, saveNow } from './session.ts';
import { errorMessage, toast } from './toast-store.ts';

interface PendingLaunch {
  file: FileSystemFileHandle;
  project: LaunchedProject;
}

/** A launched project file that needs a click to open (shown on the start screen). */
export const usePendingLaunch = create<{ pending: PendingLaunch | null }>()(() => ({ pending: null }));

export function listenForLaunchedFiles(): void {
  window.launchQueue?.setConsumer((params) => {
    const file = params.files.find((f): f is FileSystemFileHandle => f.kind === 'file');
    if (file) void handleLaunchedFile(file);
  });
}

async function handleLaunchedFile(file: FileSystemFileHandle): Promise<void> {
  if (!projectStore.inspectLaunched) return;
  let project: LaunchedProject;
  try {
    project = await projectStore.inspectLaunched(file);
  } catch (err) {
    toast.error(`Could not open ${file.name}: ${errorMessage(err)}`);
    return;
  }
  if (useEditor.getState().project?.id === project.id) return; // already open in this window
  if (project.access === 'granted') {
    try {
      await openLaunchedProject(file);
      usePendingLaunch.setState({ pending: null });
      return;
    } catch (err) {
      toast.error(`Could not open ${file.name}: ${errorMessage(err)}`);
    }
  }
  // Needs a click: leave the current project and offer it on the start screen.
  await saveNow();
  closeProject();
  window.location.hash = '';
  usePendingLaunch.setState({ pending: { file, project } });
}

/** Opens the pending launched file (call from a click: may prompt or show a folder picker). */
export async function openPendingLaunch(): Promise<void> {
  const pending = usePendingLaunch.getState().pending;
  if (!pending) return;
  try {
    if (await openLaunchedProject(pending.file)) usePendingLaunch.setState({ pending: null });
  } catch (err) {
    // E.g. the folder held a different copy: refresh what the banner offers next.
    const project = await projectStore.inspectLaunched?.(pending.file).catch(() => pending.project);
    usePendingLaunch.setState({ pending: { file: pending.file, project: project ?? pending.project } });
    throw err;
  }
}

export function dismissPendingLaunch(): void {
  usePendingLaunch.setState({ pending: null });
}
